/**
 * The level-up ceremony, as numbers.
 *
 * A sibling of the streak callout in a different key. The two have to read as
 * the same grammar — a banner that slams in, holds, and leaves — while never
 * being mistaken for each other, because they mean opposite things: a streak is
 * points in hand, a level is a milestone that pays nothing. The split is colour
 * and temperature. Streaks are gold and hot; levels are azure and cool. Neither
 * ever borrows the other's hue.
 *
 * Pure, and split from any renderer for the same reason `calloutPlan` is: the
 * display objects need a canvas, and the part worth protecting is arithmetic —
 * that a fifth level is visibly bigger than the one before it, that nothing
 * moves the whole scene, and that the ceremony's screen time stays inside a
 * budget a player meets nine or ten times a round.
 *
 * ## What is deliberately absent
 *
 * No screen shake, no dolly, no zoom, no whole-scene move of any kind, and no
 * whiteout. Adam had every whole-scene motion removed from this game once
 * already — they blur the table and the cards are the thing you are reading.
 * A level-up is emphatically not worth reintroducing one for. The ceremony is
 * additive light over a table that never moves.
 */

import { palette } from '@/core/tokens';

/** Every fifth level is the big one. */
export const MILESTONE_EVERY = 5;

/** Seconds. The banner's three phases, matching the streak callout's shape. */
const ARRIVE = 0.22;
const HOLD = 0.4;
const EXIT = 0.2;

/** Letters slam in this far apart, as the streak banner's letters do. */
const LETTER_STAGGER = 0.018;

/**
 * How long a level-up waits when a streak banner is already on screen.
 *
 * One emphasised thing at a time. A streak callout and a level banner landing
 * on the same move would overlap in the same place on the table, and the player
 * would read neither — so the level goes second, after a gap long enough to be
 * a separate beat rather than a stutter.
 */
export const QUEUE_GAP = 0.4;

export interface LevelPlan {
  level: number;
  /** True every fifth level. */
  milestone: boolean;
  /** Seconds of arrival, hold and exit. */
  arrive: number;
  hold: number;
  exit: number;
  /** Seconds between letters of the banner. */
  letterStagger: number;
  /** Final scale of the banner. Milestones are larger. */
  scale: number;
  /**
   * Scale the arrival overshoots to, as a multiple of `scale`.
   *
   * 1 on a plain level — it lands on its final size and stops. Only a milestone
   * overshoots, which is what makes the milestone legible as a bigger event
   * rather than the same event with the dials turned up.
   */
  overshoot: number;
  /** Peak brightness of the vertical sweep over the table, 0..1. */
  sweepPeak: number;
  /** How hard the rim pulse lifts, as a multiplier on the base pulse. */
  rimLift: number;
  /** Rim pulses, this many seconds apart. Milestones get a second one. */
  rimPulses: number;
  rimGap: number;
  /** Motes pushed away from the pill, in design units per second. */
  moteSpeed: number;
  /** Peak of the screen flash on the banner's leading edge, 0..1. */
  flash: number;
  /** God-rays from the banner. Milestones only; 0 seconds otherwise. */
  rays: number;
  /** Total seconds on screen. */
  screenTime: number;
}

/** The two ends of the level gradient, for anything that paints it. */
export const LEVEL_COLOUR = palette.azure;
export const LEVEL_COLOUR_BRIGHT = palette.azureLight;

/**
 * The plan for one level-up.
 *
 * Everything that separates a milestone from a plain level is in the multipliers
 * here rather than in a branch inside a timeline, so the question "is the fifth
 * one actually bigger?" has an answer that can be asserted rather than watched.
 */
export function levelPlan(level: number): LevelPlan {
  const n = Number.isFinite(level) ? Math.max(1, Math.floor(level)) : 1;
  const milestone = n % MILESTONE_EVERY === 0;

  return {
    level: n,
    milestone,
    arrive: ARRIVE,
    hold: HOLD,
    exit: EXIT,
    letterStagger: LETTER_STAGGER,
    scale: milestone ? 1.25 : 1,
    // Only the milestone overshoots. A plain level arriving past its final size
    // and settling back is the "boing" that made the old streak badge cheesy.
    overshoot: milestone ? 1.18 : 1,
    sweepPeak: milestone ? 0.44 : 0.22,
    rimLift: milestone ? 1.45 : 1,
    rimPulses: milestone ? 2 : 1,
    rimGap: 0.2,
    moteSpeed: milestone ? 420 : 300,
    flash: 0.03,
    rays: milestone ? 0.75 : 0,
    screenTime: ARRIVE + HOLD + EXIT,
  };
}
